import React, { useEffect, useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { ErrorMessage, useFormikContext } from 'formik'
import classNames from 'classnames'
import { makeStyles } from '@mui/styles'

import shortId from '@/utils/short-id'
import { FillTransition } from '@/modules/base/transitions/index'

import lang from '@/lang'

// KI-Komponenten Import
import {
  useAiTextTransform,
} from '@/modules/base/components/ai/widget/ai-text-transform-bridge'
import { isMeinBuero } from '@/utils/build-env'
const {
  emptyRequiredFieldErrorMessage: NOT_EMPTY_MESSAGE,
} = lang

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  label: {
    color: theme.text,
    fontSize: 13,
    paddingBottom: 5,
    '&[disabled]': {
      color: theme.disabledColor,
    },
  },
  input: (props) => ({
    outline: 'none',
    background: theme.backgroundColor,
    border: '1px solid',
    borderRadius: 4,
    padding: '20px 15px',
    borderColor: props.errorMessage ? theme.red : theme.borderColor,
    height: '100%',
    color: theme.text,

    '&:focus': {
      background: '#ffffff',
      borderColor: theme.activeInputBorder,
    },

    '&:disabled, &:read-only': {
      color: theme.disabledColor,
      border: `1px solid ${theme.disabledBorderColor}`,
      background: theme.backgroundColorDisabled,
    },

    '&::placeholder': {
      color: theme.textMuted,
    },
  }),
  error: {
    display: 'flex',
    justifyContent: 'flex-start',
    fontSize: '12px',
    color: theme.red,
    marginTop: '10px',
  },
}))

const isMB = isMeinBuero()
const TextAreaInput = (props) => {
  const {
    value: propValue,
    name,
    rows,
    placeholder,
    label,
    disabled,
    dataQsId,
    forwardRef,
    readOnly,
    className,
    maxLength,
    errorMessage: errorMessageText,
    selectOnFocus,
    onFocus,
    onBlur,
    onChange,
    onKeyPress,
    onKeyDown,
    fillAnimationOptions,
    useFormik,
    required,
    enableAiFeatures = true,
  } = props
  const [error, setError] = useState('')
  const classes = useStyles(props)

  const containerRef = useRef(null)
  const textareaRef = useRef(null)

  const formikContext = useFormik === undefined || useFormik ? useFormikContext() : null

  let field = null
  let errorEl = null

  const [value, setValue] = useState(propValue)

  if (formikContext) {
    field = formikContext.getFieldProps(name)
    useEffect(() => { setValue(field.value) }, [field])
    errorEl = <ErrorMessage name={name} className={classes.error} component="div" />
  } else {
    useEffect(() => { setValue(propValue) }, [propValue])
    errorEl = error ? <div className={classes.error}>{error}</div> : null
  }

  useEffect(() => {
    setError(errorMessageText)
  }, [errorMessageText])

  const elementId = `textarea-${shortId()}`

  const handleChange = (event) => {
    const { target: { value } } = event

    setError(errorMessageText || null)
    if (required && !value.trim()) {
      setError(NOT_EMPTY_MESSAGE)
    }

    setValue(value)
    if (field && field.onChange) {
      field.onChange(event)
    }
    onChange && onChange(value, name)
  }

  const handleFocus = (e) => {
    if (selectOnFocus) {
      $(e.target).select()
    }
    onFocus && onFocus(e.target)
  }

  const handleBlur = (e) => {
    const { target } = e
    const { value } = target
    const trimmedValue = value.trim()

    setValue(trimmedValue)
    onBlur && onBlur(target, trimmedValue, name)
  }

  const { renderAiComponents } = useAiTextTransform({
    containerRef,
    enabled: (!isMB && enableAiFeatures) && !readOnly && !disabled,
    inputRef: textareaRef,
    onApplyResult: useCallback((result) => {
      const textarea = textareaRef.current || forwardRef?.current
      if (!textarea) return

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const currentValue = textarea.value

      const newValue = currentValue.substring(0, start) + result + currentValue.substring(end)
      const event = {
        target: {
          value: newValue,
          name,
        },
      }

      setError(errorMessageText || null)
      if (required && !newValue.trim()) {
        setError(NOT_EMPTY_MESSAGE)
      }

      setValue(newValue)
      if (field && field.onChange) {
        field.onChange(event)
      }
      onChange && onChange(newValue, name)

      setTimeout(() => {
        const newCursorPos = start + result.length
        textarea.setSelectionRange(newCursorPos, newCursorPos)
        textarea.focus()
      }, 0)
    }, [name, forwardRef, errorMessageText, required, field, onChange]),
  })

  const containerClasses = classNames(classes.container, className)

  const labelEl = label
    ? <label htmlFor={elementId} className={classes.label} disabled={readOnly}>{label}</label>
    : null

  return (
    <div className={containerClasses} data-qs-id={dataQsId} ref={containerRef}>
      {labelEl}
      <FillTransition {...fillAnimationOptions}>
        <textarea
          id={elementId}
          className={classes.input}
          value={value}
          rows={rows}
          name={name}
          ref={(el) => {
            textareaRef.current = el
            if (forwardRef) {
              if (typeof forwardRef === 'function') {
                forwardRef(el)
              } else {
                forwardRef.current = el
              }
            }
          }}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readOnly}
        />
      </FillTransition>
      {errorEl}

      {renderAiComponents()}
    </div>
  )
}

TextAreaInput.propTypes = {
  value: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  selectOnFocus: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onKeyPress: PropTypes.func,
  onKeyDown: PropTypes.func,
  useFormik: PropTypes.bool,
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  fillAnimationOptions: PropTypes.shape({
    show: PropTypes.bool,
    color: PropTypes.string,
  }),
  rows: PropTypes.number,
  dataQsId: PropTypes.string.isRequired,
  enableAiFeatures: PropTypes.bool,
}

TextAreaInput.defaultProps = {
  value: '',
  name: '',
  label: '',
  placeholder: null,
  required: false,
  disabled: false,
  readOnly: false,
  selectOnFocus: false,
  errorMessage: null,
  fillAnimationOptions: {},
}

export default TextAreaInput
